import { FC, useState, useEffect } from 'react';
import { useAtom } from 'jotai';
import AppFrame from '../AppFrame';
import { useCurrentUser, useEvent } from '@bizzabo/entities';
import { IAppExtensionItem, ModalSize, IntegrationPointType } from '../../types/appExtension';
import useAppExtension from '../../hooks/useAppExtension';
import { useEventDetailsContext } from '../../../contexts/RingContext';
import useSessionIdFromRoute from '../../../hooks/useSessionIdFromRoute';
import { IsExpandedAppTool } from '../../../tools/atoms/appToolsAtom';
import AppModal from '../AppModal';
import { useAppExtensionTracking } from './useAppExtensionTracking';

type Props = {
    extensionId: string;
};

const AppExtension: FC<Props> = ({ extensionId }) => {
    const { eventUniqueName } = useEventDetailsContext();
    const { sessionId } = useSessionIdFromRoute();
    const { data: appExtension } = useAppExtension<IAppExtensionItem>(extensionId);
    const event = useEvent(eventUniqueName);
    const accountId = event?.data?.accountId;
    const eventId = event?.data?.id;
    const user = useCurrentUser(eventId);
    const [modalUrl, setModalUrl] = useState<string | null>(null);
    const [modalSize, setModalSize] = useState<ModalSize>(ModalSize.sm);
    const [, setIsExpanded] = useAtom(IsExpandedAppTool);

    const { AppExtensionTrack, trackAppOpenModal, trackAppOpenNewTab } = useAppExtensionTracking(
        sessionId,
        appExtension
    );

    useEffect(() => {
        if (
            !sessionId &&
            appExtension?.integrationPoint === IntegrationPointType.VENUE_SESSION_SIDE_PANEL
        ) {
            setIsExpanded(false);
        }
    }, [sessionId, appExtension]);

    const openModal = data => {
        if (data && data.url) {
            trackAppOpenModal();
            setModalUrl(data.url);
            setModalSize(data.size ?? ModalSize.sm);
        }
    };

    const closeModal = () => {
        setModalUrl(null);
        setModalSize(ModalSize.sm);
    };

    const openNewTab = data => {
        if ('url' in data) {
            trackAppOpenNewTab();
            window.open(data.url);
        }
    };

    return (
        <>
            <AppExtensionTrack>
                {appExtension && (
                    <AppFrame
                        appExtensionUrl={appExtension.contentUrl}
                        appExtensionClientId={appExtension.clientId}
                        accountId={accountId}
                        eventId={eventId}
                        sessionId={sessionId}
                        user={user?.data}
                        openModal={openModal}
                        openNewTab={openNewTab}
                    />
                )}
                {appExtension && modalUrl && (
                    <AppModal
                        url={modalUrl}
                        appExtensionUrl={appExtension.contentUrl}
                        appExtensionClientId={appExtension.clientId}
                        closeModal={closeModal}
                        size={modalSize}
                        accountId={accountId}
                        eventId={eventId}
                        user={user.data}
                    />
                )}
            </AppExtensionTrack>
        </>
    );
};

export default AppExtension;
